import { useQuery } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, CaretDown, Paperclip, Trash, type Icon } from '@/core/ui/icons';
import { useState, type ReactNode } from 'react';
import { errorMessage } from '@/core/http';
import { Button, Card, cn, Dialog, EmptyState, InlineText } from '@/core/ui';
import {
  useDeleteLesson,
  useDeleteModule,
  useRenameModule,
  useReorderLessons,
  useUpdateLesson,
} from '../application/queries';
import { courseMaterialsQuery } from '../application/materials';
import {
  formatClock,
  isPublished,
  LESSON_KIND_LABELS,
  movedLesson,
  type Course,
  type CourseModule,
  type Lesson,
} from '../domain/course';
import { materialsOfLesson, type LessonMaterial } from '../domain/materials';
import { collapseSpaces, descriptionProblem, itemTitleProblem, LIMITS } from '../domain/schemas';
import { AddLessonForm, AddModuleForm, type NewLessonVideo } from './forms';
import { LessonMaterialsPanel } from './LessonMaterialsPanel';
import { deleteProblem, editProblem } from './problems';

interface Props {
  course: Course;
  /**
   * O que cada aula mostra à direita — o backoffice injeta o envio de
   * vídeo (media) e o atalho da audiência (learning). O catalog não
   * conhece esses contextos.
   */
  lessonActions?: (lesson: Lesson) => ReactNode;
  /** Miniatura de cada aula (o pôster do vídeo, vindo do media). */
  lessonThumb?: (lesson: Lesson) => ReactNode;
  /** Clique na aula — o backoffice abre os insights dela. */
  onOpenLesson?: (lesson: Lesson) => void;
  /** Campo e envio de vídeo no formulário de nova aula (media). */
  newLessonVideo?: NewLessonVideo;
  /** Sem `cursos.editar`: só leitura — sem incluir, editar, excluir nem reordenar. */
  canEdit?: boolean;
  /** Rodapé extra de cada módulo — o backoffice injeta o envio de várias aulas (media). */
  moduleFooter?: (module: CourseModule) => ReactNode;
}

/**
 * Módulos e aulas do curso: inclusão, reordenação, títulos e descrições
 * editáveis no lugar e exclusão (aula, ou módulo vazio) com confirmação.
 */
export function CourseStructure({
  course,
  lessonActions,
  lessonThumb,
  onOpenLesson,
  newLessonVideo,
  canEdit = true,
  moduleFooter,
}: Props) {
  // Os materiais de todas as aulas numa chamada só: cada aula mostra quantos tem.
  const materials = useQuery(courseMaterialsQuery(course.id));
  return (
    <div className="space-y-4">
      {course.modules.length === 0 && (
        <EmptyState
          title="Nenhum módulo ainda"
          description={
            canEdit
              ? 'Crie o primeiro módulo para começar a adicionar aulas.'
              : 'Este curso ainda não tem aulas.'
          }
        />
      )}
      {course.modules.map((m, i) => (
        <ModuleCard
          key={m.id}
          index={i}
          courseId={course.id}
          module={m}
          lessonActions={lessonActions}
          lessonThumb={lessonThumb}
          onOpenLesson={onOpenLesson}
          newLessonVideo={newLessonVideo}
          canEdit={canEdit}
          materials={materials.data}
          footer={moduleFooter?.(m)}
          // Mesma regra do agregado: curso publicado não fica sem aulas.
          keepsLastLesson={isPublished(course) && course.lesson_count === 1}
        />
      ))}
      {canEdit && (
        <Card className="p-4">
          <AddModuleForm courseId={course.id} />
        </Card>
      )}
    </div>
  );
}

function ModuleCard({
  index,
  courseId,
  module,
  lessonActions,
  lessonThumb,
  onOpenLesson,
  newLessonVideo,
  canEdit,
  keepsLastLesson,
  materials,
  footer,
}: {
  index: number;
  courseId: string;
  module: CourseModule;
  lessonActions?: (lesson: Lesson) => ReactNode;
  lessonThumb?: (lesson: Lesson) => ReactNode;
  onOpenLesson?: (lesson: Lesson) => void;
  newLessonVideo?: NewLessonVideo;
  canEdit: boolean;
  keepsLastLesson: boolean;
  materials?: LessonMaterial[];
  footer?: ReactNode;
}) {
  const reorder = useReorderLessons(courseId);
  const rename = useRenameModule(courseId);
  const updateLesson = useUpdateLesson(courseId);
  const deleteLesson = useDeleteLesson(courseId);
  const deleteModule = useDeleteModule(courseId);
  const [lessonToDelete, setLessonToDelete] = useState<Lesson | null>(null);
  const [confirmModule, setConfirmModule] = useState(false);
  // Aulas com o painel de materiais aberto.
  const [openMaterials, setOpenMaterials] = useState<ReadonlySet<string>>(new Set());
  const ids = module.lessons.map((l) => l.id);
  const toggleMaterials = (lessonId: string) =>
    setOpenMaterials((open) => {
      const next = new Set(open);
      if (!next.delete(lessonId)) next.add(lessonId);
      return next;
    });
  const total = module.lessons.reduce((n, l) => n + l.duration_s, 0);

  const move = (id: string, delta: -1 | 1) =>
    reorder.mutate({ moduleId: module.id, ids: movedLesson(ids, id, delta) });

  const saveLesson = (lessonId: string, changes: { title?: string; description?: string }) =>
    updateLesson.mutateAsync({ lessonId, changes });

  const closeLessonDialog = () => {
    deleteLesson.reset();
    setLessonToDelete(null);
  };
  const closeModuleDialog = () => {
    deleteModule.reset();
    setConfirmModule(false);
  };

  return (
    <Card className="overflow-hidden">
      <header className="flex items-baseline gap-3 border-b border-line px-6 py-4">
        <h3 className="flex min-w-0 flex-1 items-baseline text-base font-semibold">
          <span className="mr-2 text-fg-subtle tabular-nums">{index + 1}.</span>
          <InlineText
            label="título do módulo"
            value={module.title}
            readOnly={!canEdit}
            maxLength={LIMITS.title}
            normalize={collapseSpaces}
            validate={itemTitleProblem}
            describeError={editProblem}
            onSave={(title) => rename.mutateAsync({ moduleId: module.id, title })}
            className="truncate"
          />
        </h3>
        <span className="text-sm text-fg-subtle tabular-nums">
          {module.lessons.length} {module.lessons.length === 1 ? 'aula' : 'aulas'} · {formatClock(total)}
        </span>
        {/* Só módulo vazio se exclui: as aulas saem uma a uma, cada uma uma decisão. */}
        {canEdit && module.lessons.length === 0 && (
          <IconButton
            label={`Excluir módulo "${module.title}"`}
            icon={Trash}
            onClick={() => setConfirmModule(true)}
          />
        )}
      </header>

      {reorder.isError && <p className="px-6 pt-3 text-sm text-fg">{errorMessage(reorder.error)}</p>}

      <ol className={cn('divide-y divide-line', reorder.isPending && 'opacity-60')}>
        {module.lessons.map((l, i) => {
          const lessonMaterials = materialsOfLesson(materials, l.id);
          const materialsOpen = openMaterials.has(l.id);
          return (
            <li key={l.id} className="group px-6 py-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="flex min-w-0 flex-1 items-center gap-4">
                  {lessonThumb?.(l)}
                  <div className="min-w-0 flex-1">
                    <InlineText
                      label={`título da aula "${l.title}"`}
                      value={l.title}
                      readOnly={!canEdit}
                      maxLength={LIMITS.title}
                      normalize={collapseSpaces}
                      validate={itemTitleProblem}
                      describeError={editProblem}
                      onSave={(title) => saveLesson(l.id, { title })}
                      className="truncate text-base font-medium"
                      // Com insights, clicar no título abre a aula; editar fica no lápis.
                      trigger={onOpenLesson ? 'pencil' : 'text'}
                      display={
                        onOpenLesson ? (
                          <button
                            type="button"
                            onClick={() => onOpenLesson(l)}
                            title="Ver insights da aula"
                            className="block max-w-full truncate text-left text-base font-medium text-fg hover:text-brand-600"
                          >
                            {l.title}
                          </button>
                        ) : undefined
                      }
                    />
                    <p className="mt-0.5 text-sm text-fg-subtle">
                      {LESSON_KIND_LABELS[l.kind] ?? l.kind} ·{' '}
                      {l.kind === 'video' && !l.asset_id ? (
                        'sem vídeo ainda'
                      ) : (
                        <span className="tabular-nums">{formatClock(l.duration_s)}</span>
                      )}
                      {!l.required && ' · opcional'}
                    </p>
                    {(canEdit || l.description) && (
                      // Sem descrição, o convite para escrever uma só aparece ao passar o mouse.
                      <div
                        className={cn(
                          'mt-1',
                          !l.description &&
                            'opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100',
                        )}
                      >
                        <InlineText
                          multiline
                          label={`descrição da aula "${l.title}"`}
                          value={l.description}
                          placeholder="Adicionar descrição"
                          readOnly={!canEdit}
                          maxLength={LIMITS.description}
                          validate={descriptionProblem}
                          describeError={editProblem}
                          onSave={(description) => saveLesson(l.id, { description })}
                          className="text-sm whitespace-pre-line text-fg-muted"
                        />
                      </div>
                    )}
                  </div>
                </div>
                {(canEdit || lessonMaterials.length > 0) && (
                  <MaterialsToggle
                    count={lessonMaterials.length}
                    open={materialsOpen}
                    title={l.title}
                    onClick={() => toggleMaterials(l.id)}
                  />
                )}
                {lessonActions && <div className="flex items-center gap-2">{lessonActions(l)}</div>}
                {canEdit && (
                  // Reordenar e excluir só aparecem ao passar o mouse: são raros e não precisam pesar na tela.
                  <div className="flex gap-0.5 opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100">
                    {module.lessons.length > 1 && (
                      <>
                        <MoveButton
                          label={`Subir "${l.title}"`}
                          icon={ArrowUp}
                          disabled={i === 0 || reorder.isPending}
                          onClick={() => move(l.id, -1)}
                        />
                        <MoveButton
                          label={`Descer "${l.title}"`}
                          icon={ArrowDown}
                          disabled={i === module.lessons.length - 1 || reorder.isPending}
                          onClick={() => move(l.id, 1)}
                        />
                      </>
                    )}
                    <IconButton
                      label={`Excluir "${l.title}"`}
                      icon={Trash}
                      onClick={() => setLessonToDelete(l)}
                    />
                  </div>
                )}
              </div>
              {materialsOpen && (
                <div className="mt-3">
                  <LessonMaterialsPanel
                    courseId={courseId}
                    lessonId={l.id}
                    lessonTitle={l.title}
                    materials={lessonMaterials}
                    canEdit={canEdit}
                  />
                </div>
              )}
            </li>
          );
        })}
      </ol>

      {module.lessons.length === 0 && (
        <p className="px-6 py-4 text-sm text-fg-subtle">Nenhuma aula neste módulo ainda.</p>
      )}

      {canEdit && (
        // Lado a lado quando fechados; aberto, cada um ocupa a largura toda.
        <div className="flex flex-wrap items-start gap-x-2 gap-y-3 border-t border-line px-6 py-3">
          <AddLessonForm courseId={courseId} moduleId={module.id} video={newLessonVideo} />
          {footer}
        </div>
      )}

      <ConfirmDelete
        open={!!lessonToDelete}
        title="Excluir aula?"
        name={lessonToDelete?.title}
        confirmLabel="Excluir aula"
        blocked={
          keepsLastLesson
            ? 'Esta é a única aula de um curso publicado. Adicione outra antes ou arquive o curso.'
            : null
        }
        pending={deleteLesson.isPending}
        error={deleteLesson.isError ? deleteProblem(deleteLesson.error) : null}
        onClose={closeLessonDialog}
        onConfirm={() =>
          lessonToDelete && deleteLesson.mutate(lessonToDelete.id, { onSuccess: closeLessonDialog })
        }
      >
        A aula sai do curso e deixa de aparecer para os alunos. O progresso de quem já assistiu fica no
        histórico, mas não conta mais. Não dá para desfazer.
      </ConfirmDelete>

      <ConfirmDelete
        open={confirmModule}
        title="Excluir módulo?"
        name={module.title}
        confirmLabel="Excluir módulo"
        pending={deleteModule.isPending}
        error={deleteModule.isError ? deleteProblem(deleteModule.error) : null}
        onClose={closeModuleDialog}
        onConfirm={() => deleteModule.mutate(module.id, { onSuccess: closeModuleDialog })}
      >
        O módulo está vazio e sai do curso. Não dá para desfazer.
      </ConfirmDelete>
    </Card>
  );
}

function ConfirmDelete({
  open,
  title,
  name,
  confirmLabel,
  blocked = null,
  pending,
  error,
  onClose,
  onConfirm,
  children,
}: {
  open: boolean;
  title: string;
  name?: string;
  confirmLabel: string;
  /** Por que não dá para excluir — no lugar do botão de confirmar. */
  blocked?: string | null;
  pending: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: () => void;
  children: ReactNode;
}) {
  return (
    <Dialog open={open} onClose={onClose} title={title} description={name}>
      <p className="text-sm text-fg-muted">{blocked ?? children}</p>
      {error && (
        <p role="alert" className="mt-3 text-sm text-fg">
          {error}
        </p>
      )}
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>
          {blocked ? 'Entendi' : 'Cancelar'}
        </Button>
        {!blocked && (
          <Button variant="danger" loading={pending} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        )}
      </div>
    </Dialog>
  );
}

/** "Materiais · 2": abre o painel da aula. Sem material, só aparece ao passar o mouse. */
function MaterialsToggle({
  count,
  open,
  title,
  onClick,
}: {
  count: number;
  open: boolean;
  title: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={open}
      aria-label={`Materiais de "${title}"${count ? ` (${count})` : ''}`}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm text-fg-subtle hover:bg-surface-2 hover:text-fg',
        open || count > 0
          ? 'opacity-100'
          : 'opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100',
        open && 'bg-surface-2 text-fg',
      )}
    >
      <Paperclip aria-hidden className="size-4" />
      {count > 0 ? <span className="tabular-nums">{count}</span> : <span>Materiais</span>}
      <CaretDown aria-hidden className={cn('size-3 transition-transform', open && 'rotate-180')} />
    </button>
  );
}

function IconButton({ label, icon: Icon, onClick }: { label: string; icon: Icon; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className="rounded-md p-1.5 text-fg-subtle hover:bg-surface-2 hover:text-fg"
    >
      <Icon className="size-3.5" />
    </button>
  );
}

function MoveButton({
  label,
  icon: Icon,
  disabled,
  onClick,
}: {
  label: string;
  icon: Icon;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="rounded-md p-1.5 text-fg-subtle hover:bg-surface-2 hover:text-fg disabled:cursor-default disabled:opacity-30 disabled:hover:bg-transparent"
    >
      <Icon className="size-3.5" />
    </button>
  );
}
